import { Box } from '@mui/material';
import React from 'react';
import {
  ArrayInput,
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  DeleteButton,
  DeleteWithConfirmButton,
  Edit,
  EditButton,
  Filter,
  FunctionField,
  ImageField,
  required,
  SaveButton,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  SimpleFormIterator,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import {
  AdminExerciseDetailsFragment,
  Prefix,
  useExerciseUnitsQuery,
} from '../../graphql/types';
import useCanWrite from '../../hooks/useCanWrite';
import useIsExerciseEditor from '../../hooks/useIsExerciseEditor';
import { IDSearch } from '../reusable/IDSearch';
import { ResourceLink } from '../reusable/ResourceLink';
import ClipboardField from './components/ClipboardField';
import FrameCapturePreview from './components/FrameCapturePreview';
import ImageUploadInput from './components/ImageUploadInput';
import InfiniteList from './components/InfiniteList';
import VideoUploadInput from './components/VideoUploadInput';

const ExerciseFilters: React.FC = (props) => (
  <Filter {...props}>
    <IDSearch label="Name" source="name" alwaysOn />
  </Filter>
);

export const ExerciseList: React.FC = () => {
  const isExerciseEditor = useIsExerciseEditor();
  const canWrite = useCanWrite();
  return (
    <InfiniteList filters={<ExerciseFilters />}>
      <Datagrid bulkActionButtons={false}>
        <TextField source="name" sortable={false} />
        <TextField source="displayName" sortable={false} />
        <BooleanField source="canTrackWeight" sortable={false} />
        <TextField
          source="defaultUnit.name"
          label="Default Unit"
          sortable={false}
        />
        {(canWrite || isExerciseEditor) && <EditButton />}
        <ShowButton />
        {(canWrite || isExerciseEditor) && <DeleteWithConfirmButton />}
      </Datagrid>
    </InfiniteList>
  );
};

const ExerciseShowActions = () => {
  const record = useRecordContext<AdminExerciseDetailsFragment>();

  return (
    <TopToolbar>
      <EditButton disabled={record?.isDeleted} />
      <DeleteWithConfirmButton disabled={record?.isDeleted} />
    </TopToolbar>
  );
};

const ExerciseDeletedMessage: React.FC = () => {
  return (
    <Box sx={{ marginBottom: 2, color: 'red' }}>
      This exercise has been deleted.{' '}
      <ResourceLink name="Exercise" type="list">
        Back to exercise list
      </ResourceLink>
    </Box>
  );
};

const ExerciseShowView: React.FC = () => {
  const record = useRecordContext<AdminExerciseDetailsFragment>();
  const isDeleted = record?.isDeleted;

  return (
    <SimpleShowLayout>
      {isDeleted && <ExerciseDeletedMessage />}

      <ClipboardField source="id" fontSize={14} />
      <TextField source="name" label="Name" />
      <TextField source="displayName" />
      <FunctionField
        key="coachingKeys"
        label="Instructions"
        render={(exerciseRecord) => {
          return (
            <ul>
              {exerciseRecord.coachingKeys?.map((key: string) => (
                <li key={key}>{key}</li>
              ))}
            </ul>
          );
        }}
      />
      <TextField
        source="modifications"
        label="Modifications"
        style={{ whiteSpace: 'pre' }}
      />
      <TextField
        source="coachNotes"
        label="Coach Notes"
        style={{ whiteSpace: 'pre' }}
      />
      <TextField source="defaultUnit.name" label="Default Unit" />
      <BooleanField source="canTrackWeight" label="Can Track Weight" />
      <TextField source="apmlId" label="APML ID" />
      <FunctionField
        source="video"
        label="Video"
        render={(record) => {
          if (!record.video) {
            return null;
          }
          return (
            // biome-ignore lint/a11y/useMediaCaption: No captions for exercise videos
            <video
              src={record.video.url}
              controls
              style={{ width: '100%', maxWidth: 480 }}
            />
          );
        }}
      />
      <ImageField
        source="thumbnail.url"
        label="Thumbnail"
        sx={{
          '& img': {
            width: '100% !important',
            maxWidth: 480,
            height: 'auto !important',
            margin: '0 !important',
          },
        }}
      />
    </SimpleShowLayout>
  );
};
export const ExerciseShow: React.FC = () => {
  return (
    <Show actions={<ExerciseShowActions />}>
      <ExerciseShowView />
    </Show>
  );
};

const ExerciseEditToolbar: React.FC = () => {
  const record = useRecordContext<AdminExerciseDetailsFragment>();
  const { isDeleted } = record || {};

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <SaveButton disabled={isDeleted} />
      <DeleteButton mutationMode="pessimistic" disabled={isDeleted} />
    </Toolbar>
  );
};

const ExerciseEditView: React.FC = () => {
  const record = useRecordContext<AdminExerciseDetailsFragment>();
  const { isDeleted } = record || {};
  const [capturedFrame, setCapturedFrame] = React.useState<string | null>(null);

  const { data: exerciseUnitsData, loading: defaultUnitsLoading } =
    useExerciseUnitsQuery();
  const defaultUnits = (exerciseUnitsData?.exerciseUnits || []).map((unit) => ({
    id: unit.value,
    name: unit.name,
  }));

  const handleFrameCapture = React.useCallback((dataUrl: string) => {
    setCapturedFrame(dataUrl);
  }, []);

  const handleReplaceThumbnail = React.useCallback(() => {
    setCapturedFrame(null);
  }, []);

  const handleClearFrame = React.useCallback(() => {
    setCapturedFrame(null);
  }, []);

  return (
    <SimpleForm toolbar={<ExerciseEditToolbar />}>
      {isDeleted && <ExerciseDeletedMessage />}
      <TextInput source="id" slotProps={{ input: { disabled: true } }} />
      <TextInput source="apmlId" disabled />
      <TextInput source="name" fullWidth required disabled={isDeleted} />
      <TextInput
        source="displayName"
        fullWidth
        disabled={isDeleted}
        helperText='This will override "Name" in the app. If empty, "Name" will be used.'
        sx={{ marginBottom: 4 }}
      />
      <ArrayInput source="coachingKeys" label="Instructions">
        <SimpleFormIterator disabled={isDeleted}>
          <TextInput
            source=""
            fullWidth
            label="Instruction"
            style={{ width: '50vw' }}
          />
        </SimpleFormIterator>
      </ArrayInput>
      <TextInput
        source="modifications"
        fullWidth
        multiline
        disabled={isDeleted}
        label="Modifications"
      />
      <TextInput
        source="coachNotes"
        fullWidth
        multiline
        disabled={isDeleted}
        label="Coach Notes"
      />
      <SelectInput
        isPending={defaultUnitsLoading}
        source="defaultUnit.value"
        label="Default Unit"
        choices={defaultUnits}
        disabled={isDeleted}
      />
      <BooleanInput source="canTrackWeight" disabled={isDeleted} />
      <BooleanInput source="isDeleted" disabled />
      <VideoUploadInput
        placeholder="Drop a video file to upload, or click to select it."
        multiple={false}
        prefix={Prefix.ExerciseVideo}
        source="video"
        onFrameCapture={handleFrameCapture}
      />
      <FrameCapturePreview
        capturedFrame={capturedFrame}
        onReplaceThumbnail={handleReplaceThumbnail}
        onClearFrame={handleClearFrame}
        thumbnailSource="thumbnail"
      />
      <ImageUploadInput
        placeholder="Drop an image file to upload, or click to select it."
        multiple={false}
        prefix={Prefix.ExerciseThumbnail}
        source="thumbnail"
      />
    </SimpleForm>
  );
};

export const ExerciseEdit: React.FC = () => {
  return (
    <Edit mutationMode="optimistic" redirect={false}>
      <ExerciseEditView />
    </Edit>
  );
};

export const ExerciseCreate: React.FC = () => {
  const [capturedFrame, setCapturedFrame] = React.useState<string | null>(null);

  const { data: exerciseUnitsData, loading: defaultUnitsLoading } =
    useExerciseUnitsQuery();
  const defaultUnits = (exerciseUnitsData?.exerciseUnits || []).map((unit) => ({
    id: unit.value,
    name: unit.name,
  }));

  const handleFrameCapture = React.useCallback((dataUrl: string) => {
    setCapturedFrame(dataUrl);
  }, []);

  const handleReplaceThumbnail = React.useCallback(() => {
    setCapturedFrame(null);
  }, []);

  const handleClearFrame = React.useCallback(() => {
    setCapturedFrame(null);
  }, []);

  return (
    <Create>
      <SimpleForm>
        <TextInput source="name" label="Name" validate={required()} fullWidth />
        <TextInput
          source="displayName"
          fullWidth
          helperText='This will override "Name" in the app. If empty, "Name" will be used.'
          sx={{ marginBottom: 4 }}
        />
        <ArrayInput
          source="coachingKeys"
          label="Instructions"
          aria-label="Instructions"
        >
          <SimpleFormIterator>
            <TextInput
              source=""
              fullWidth
              label="Instructions"
              style={{ width: '50vw' }}
            />
          </SimpleFormIterator>
        </ArrayInput>
        <TextInput
          source="modifications"
          fullWidth
          multiline
          label="Modifications"
        />
        <TextInput
          source="coachNotes"
          fullWidth
          multiline
          label="Coach Notes"
        />
        <SelectInput
          isPending={defaultUnitsLoading}
          source="defaultUnit.value"
          label="Default Unit"
          choices={defaultUnits}
        />
        <BooleanInput
          source="canTrackWeight"
          label="Can Track Weight"
          defaultValue={false}
        />
        <VideoUploadInput
          placeholder="Drop a video file to upload, or click to select it."
          multiple={false}
          prefix={Prefix.ExerciseVideo}
          source="video"
          onFrameCapture={handleFrameCapture}
        />
        <FrameCapturePreview
          capturedFrame={capturedFrame}
          onReplaceThumbnail={handleReplaceThumbnail}
          onClearFrame={handleClearFrame}
          thumbnailSource="thumbnail"
        />
        <ImageUploadInput
          placeholder="Drop an image file to upload, or click to select it."
          multiple={false}
          prefix={Prefix.ExerciseThumbnail}
          source="thumbnail"
        />
      </SimpleForm>
    </Create>
  );
};
